// import isoCodeToCountry from '~/modules/utils/iso-code-to-country';
// import eurovisionIcon from '~/routes/sing-a-song/song-selection/components/song-card/eurovision-icon.svg';
import { ReactElement, ReactNode, useMemo } from 'react';

import { SongPreview } from '~/interfaces';
import { Icon } from '~/modules/elements/akui/icon';
import Typography from '~/modules/elements/akui/primitives/typography';
import { ClosableTooltip } from '~/modules/elements/tooltip';
import useRemoteMicServerStatus from '~/modules/remote-mic/hooks/use-remote-mic-server-status';
import { useLanguageList } from '~/modules/songs/hooks/use-language-list';
import useRemoteMicSongList from '~/modules/songs/hooks/use-remote-mic-song-list';
import { useSetlist } from '~/modules/songs/hooks/use-setlist';
import { useSungSongs } from '~/modules/songs/stats/hooks';
import { SongGroup } from '~/routes/sing-a-song/song-selection/hooks/use-song-list';
import { AppliedFilters } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

export const LANGUAGE_PLAYLIST_PREFIX = 'language-';

export interface PlaylistEntry {
  name: string;
  display?: ReactNode;
  /**
   * Plain-text label for the remote mic's mirrored playlist picker. `display` can't be sent (it's
   * JSX — logos, tooltips), so an entry whose `name` isn't presentable on its own supplies one.
   */
  remoteLabel?: string;
  hideNew?: boolean;
  /** True when this entry opens the language picker sheet rather than directly activating a playlist. */
  isLanguagePicker?: boolean;
  Wrapper?: (props: { children: ReactElement; focused: boolean; active: boolean }) => ReactNode;
  filters: AppliedFilters;
  groupData?: (song: SongPreview) => Pick<SongGroup, 'name' | 'displayShort' | 'displayLong' | 'remoteLabel'>;
  postGrouping?: (groups: SongGroup[]) => SongGroup[];
  sortingFn?: (a: SongPreview, b: SongPreview) => number;
  footerComponent?: ReactNode;
}

export const usePlaylists = (
  songs: SongPreview[],
  recommended: string[],
  isLoading: boolean,
  extraLanguage: string | null,
): PlaylistEntry[] => {
  const { isSetlistInPlace } = useSetlist();
  const songLanguages = useLanguageList(songs);
  const remoteSongList = useRemoteMicSongList();
  const { connected } = useRemoteMicServerStatus();
  const sungSongs = useSungSongs();

  return useMemo<PlaylistEntry[]>(() => {
    if (isLoading) return [];

    const selection: PlaylistEntry = {
      name: 'Selection',
      Wrapper: ({ children, active, focused }) => (
        <ClosableTooltip
          {...(active && focused ? { open: true } : {})}
          oneTime={false}
          timeoutMs={15_000}
          dismissKey="selection-playlist"
          place={'right'}
          title={
            <>
              A combination of songs you might like and popular with other players.
              <br />
              <br /> <strong>Search</strong> is performed across all the songs.
            </>
          }>
          {children}
        </ClosableTooltip>
      ),
      display: (
        <span>
          <strong>★</strong>&nbsp;Selection
        </span>
      ),
      remoteLabel: '★ Selection',
      filters: {
        specificSongs: recommended,
      },
    };

    const all: PlaylistEntry = { name: 'All', filters: {} };
    const playlists: Array<PlaylistEntry | null> = [
      !isSetlistInPlace ? selection : null, // makes "all" playlist the first one if a setlist is loaded
      all,
      sungSongs.length > 0
        ? {
            name: 'sung-songs',
            display: 'Sung songs',
            remoteLabel: 'Sung songs',
            filters: { specificSongs: sungSongs },
          }
        : null,
      // {
      //   name: 'Halloween',
      //   display: (
      //     <>
      //       <span style={{ color: colorSets.halloweenViolet.text }}>Hallo</span>
      //       <span style={{ color: colorSets.halloweenOrange.text }}>ween</span> 🎃
      //     </>
      //   ),
      //   filters: { edition: 'halloween' },
      // },
      songLanguages[0]
        ? ({
            name: songLanguages[0].name,
            filters: { language: songLanguages[0].name },
          } as PlaylistEntry)
        : null,
      songLanguages[1]
        ? {
            name: songLanguages[1].name,
            filters: { language: songLanguages[1].name },
          }
        : null,
      { name: 'Duets', filters: { duet: true } },
      !isSetlistInPlace
        ? {
            name: 'New',
            filters: { recentlyUpdated: true },
          }
        : null,
      connected.length > 0
        ? {
            name: 'remote-mics',
            display: <>From &nbsp;Phones</>,
            remoteLabel: 'From Phones',
            filters: { specificSongs: remoteSongList, skipExcludedLanguages: true },
            footerComponent: (
              <div data-test="remote-mic-playlist-tip" className="flex justify-center">
                <Typography className="text-xl">
                  Add songs on your <strong>phone</strong> using{' '}
                  <strong>
                    <Icon icon="ic:baseline-list" className="text-[1em]!" /> Song List
                  </strong>{' '}
                  tab on the bottom of the screen
                </Typography>
              </div>
            ),
          }
        : null,
      // If the user has previously picked a language via the picker, show that language as the active entry.
      // Otherwise, show the "More languages" placeholder that will open the picker.
      extraLanguage !== null
        ? ({
            name: `${LANGUAGE_PLAYLIST_PREFIX}${extraLanguage}`,
            display: extraLanguage,
            remoteLabel: extraLanguage,
            isLanguagePicker: true,
            filters: { language: extraLanguage, skipExcludedLanguages: true },
          } as PlaylistEntry)
        : ({
            name: 'more-languages',
            display: '🌐 More languages',
            isLanguagePicker: true,
            // Empty filters are a safe fallback — the playlist name 'more-languages' is never persisted
            // because clicking it opens the language picker instead of activating it directly.
            filters: {},
          } as PlaylistEntry),
    ];

    return playlists.filter((playlist): playlist is PlaylistEntry => playlist !== null);
  }, [
    songLanguages,
    isLoading,
    recommended,
    remoteSongList,
    connected.length,
    isSetlistInPlace,
    extraLanguage,
    sungSongs,
  ]);
};
